/** @format */

import React, { useState, useEffect, Fragment } from "react";
import { IAccountEdgeDeployments } from "./AccountEdgeDeployments.types";
import VoiceGainApplicationApi from "../../../../apis/VoiceGainApplicationApi";
import { Table, Tag, Input, Button, Spin, message, Tooltip } from "antd";
import Column from "antd/lib/table/Column";
import { ColumnProps } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import K8sModal from "./K8sModal";
import { useFetchEdgeDeployments } from "../../../../apis/swr/fetchers";

interface IAccountEdgeDeploymentsProps {
	params: { id: string };
}

const AccountEdgeDeployments: React.FC<IAccountEdgeDeploymentsProps> = ({
	params,
}) => {
	const { edgeDeployments, isLoading } = useFetchEdgeDeployments();
	const [accountIds, setAccountIds] = useState<string[]>([]);
	const [searchText, setSearchText] = useState<string>("");

	useEffect(() => {
		if (edgeDeployments) {
			const idArr: string[] = [];
			edgeDeployments.map((edgeDeployment) => {
				return idArr.push(edgeDeployment.accountId);
			});
			setAccountIds(idArr);
		}
	}, [edgeDeployments]);

	const getColumnSearchProps = (
		dataIndex: string
	): {
		filterDropdown: ColumnProps<IAccountEdgeDeployments>["filterDropdown"];
		filterIcon: ColumnProps<IAccountEdgeDeployments>["filterIcon"];
		onFilter: ColumnProps<IAccountEdgeDeployments>["onFilter"];
		render: ColumnProps<IAccountEdgeDeployments>["render"];
	} => ({
		filterDropdown: ({
			setSelectedKeys,
			selectedKeys,
			confirm,
			clearFilters,
		}) => {
			if (selectedKeys && setSelectedKeys) {
				return (
					<div style={{ padding: 8 }}>
						<Input
							placeholder={`Search`}
							value={selectedKeys[0]}
							onChange={(e) =>
								setSelectedKeys(e.target.value ? [e.target.value] : [])
							}
							onPressEnter={() => handleSearch(selectedKeys, confirm)}
							style={{ width: 188, marginBottom: 8, display: "block" }}
						/>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90, marginRight: 8 }}>
							Search
						</Button>
						<Button
							onClick={() => handleReset(clearFilters)}
							size="small"
							style={{ width: 90 }}>
							Reset
						</Button>
					</div>
				);
			}
		},
		filterIcon: (filtered) => (
			<SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
		),
		onFilter: (value, record) =>
			record[dataIndex]
				.toString()
				.toLowerCase()
				.includes(value.toString().toLowerCase()),
		render: (text: string, record: IAccountEdgeDeployments) => (
			<Fragment>
				<Tooltip title={`${record.clusterId}`}>
					<Highlighter
						highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
						searchWords={[searchText]}
						autoEscape
						textToHighlight={text.toString()}
					/>
				</Tooltip>
			</Fragment>
		),
	});

	const handleSearch = (selectedKeys, confirm) => {
		confirm();
		setSearchText(selectedKeys[0]);
	};

	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText("");
	};

	if (accountIds.includes(params.id)) {
		return (
			<Spin spinning={isLoading}>
				<div>
					<Table dataSource={edgeDeployments} rowKey={(record) => record.name}>
						<Column
							title="Name"
							dataIndex="name"
							key="name"
							width="20%"
							{...getColumnSearchProps("name")}
						/>
						<Column
							title="Status"
							dataIndex="status"
							key="status"
							align="center"
							width="15%"
							sorter={(a: { status: string }, b: { status: string }) =>
								("" + a.status).localeCompare(b.status)
							}
							onFilter={(
								value: string | number | boolean,
								record: { status: string }
							) => {
								const typedRecord = record as IAccountEdgeDeployments;

								if (typedRecord.status) {
									return typedRecord.status.indexOf(value.toString()) === 0;
								}
								return false;
							}}
							filters={[
								{ text: "MODIFIED", value: "MODIFIED" },
								{ text: "STARTING", value: "STARTING" },
								{ text: "PROCESSING", value: "PROCESSING" },
								{ text: "READY", value: "READY" },
								{ text: "ERROR", value: "ERROR" },
							]}
							render={(text) => {
								if (text === "MODIFIED")
									return <Tag color="purple">MODIFIED</Tag>;
								if (text === "STARTING")
									return <Tag color="gold">STARTING</Tag>;
								if (text === "PROCESSING")
									return <Tag color="blue">PROCESSING</Tag>;
								if (text === "READY") return <Tag color="green">READY</Tag>;
								if (text === "ERROR") return <Tag color="red">ERROR</Tag>;
							}}
						/>
						<Column
							title="Type"
							dataIndex="type"
							key="type"
							align="center"
							width="15%"
							onFilter={(
								type: string | number | boolean,
								record: IAccountEdgeDeployments
							) => {
								if (record.type) {
									return record.type.indexOf(type.toString()) === 0;
								}
								return false;
							}}
							filters={[
								{ text: "EZ", value: "EZ" },
								{ text: "User Configured", value: "UserConfigured" },
							]}
							render={(text) => {
								if (text === "EZ") return <Tag color="geekblue">EZ</Tag>;
								if (text === "UserConfigured")
									return <Tag color="orange">User Configured</Tag>;
							}}
						/>
						<Column
							title="Uses Offline Models"
							dataIndex="usesOffLineModels"
							key="usesOffLineModels"
							align="center"
							onFilter={(
								usesOffLineModels: string | number | boolean,
								record: IAccountEdgeDeployments
							) => {
								return (
									record.usesOffLineModels
										.toString()
										.indexOf(usesOffLineModels.toString()) === 0
								);
							}}
							filters={[
								{ text: "true", value: "true" },
								{ text: "false", value: "false" },
							]}
							render={(text) => {
								if (text === true)
									return <Tag color="green">{text.toString()}</Tag>;
								else if (text === false)
									return <Tag color="red">{text.toString()}</Tag>;
							}}
						/>
						<Column
							title="Uses Realtime Models"
							dataIndex="usesRealTimeModels"
							key="usesRealTimeModels"
							align="center"
							onFilter={(
								usesRealTimeModels: string | number | boolean,
								record: IAccountEdgeDeployments
							) => {
								return (
									record.usesRealTimeModels
										.toString()
										.indexOf(usesRealTimeModels.toString()) === 0
								);
							}}
							filters={[
								{ text: "true", value: "true" },
								{ text: "false", value: "false" },
							]}
							render={(text) => {
								if (text === true)
									return <Tag color="green">{text.toString()}</Tag>;
								else if (text === false)
									return <Tag color="red">{text.toString()}</Tag>;
							}}
						/>
						<Column
							title="Transmogrifier"
							dataIndex="transmogrifier"
							key="transmogrifier"
							align="center"
							onFilter={(
								transmogrifier: string | number | boolean,
								record: IAccountEdgeDeployments
							) => {
								return (
									record.transmogrifier
										.toString()
										.indexOf(transmogrifier.toString()) === 0
								);
							}}
							filters={[
								{ text: "true", value: "true" },
								{ text: "false", value: "false" },
							]}
							render={(text) => {
								if (text === true)
									return <Tag color="green">{text.toString()}</Tag>;
								else if (text === false)
									return <Tag color="red">{text.toString()}</Tag>;
							}}
						/>
						<Column
							title="Date Created"
							dataIndex="dateCreated"
							key="dateCreated"
							align="center"
							render={(text: string) => {
								if (text) {
									return text.split("T")[0];
								}
							}}
						/>
						<Column
							title="K8s"
							key="k8s"
							render={(text, record: IAccountEdgeDeployments) => {
								return (
									<K8sModal
										k8sExtIp={record.k8sExtIp}
										k8sExtPort={record.k8sExtPort}
										k8sNodes={record.k8sNodes}
									/>
								);
							}}
						/>
					</Table>
				</div>
			</Spin>
		);
	} else
		return (
			<Spin spinning={isLoading}>
				<div></div>
			</Spin>
		);
};

export default AccountEdgeDeployments;
